import React from 'react';
import { useComparison } from '../hooks/useMarketData';
import { formatPrice, formatPercentage } from '../utils/formatters';

export const HistoricalView: React.FC = () => {
  const { data, isLoading, error } = useComparison(['1d', '1mo', '3mo', '1y']);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="text-red-600">Failed to load comparison data</div>
      </div>
    );
  }

  const periodLabels: Record<string, string> = {
    '1d': '1 Day',
    '1mo': '1 Month',
    '3mo': '3 Months',
    '1y': '1 Year',
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Historical Performance</h3>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-2 text-xs font-medium text-gray-600 uppercase">Period</th>
              <th className="text-right py-3 px-2 text-xs font-medium text-gray-600 uppercase">Start</th>
              <th className="text-right py-3 px-2 text-xs font-medium text-gray-600 uppercase">End</th>
              <th className="text-right py-3 px-2 text-xs font-medium text-gray-600 uppercase">Change</th>
              <th className="text-right py-3 px-2 text-xs font-medium text-gray-600 uppercase">% Change</th>
              <th className="text-right py-3 px-2 text-xs font-medium text-gray-600 uppercase">High</th>
              <th className="text-right py-3 px-2 text-xs font-medium text-gray-600 uppercase">Low</th>
              <th className="text-right py-3 px-2 text-xs font-medium text-gray-600 uppercase">Data Points</th>
            </tr>
          </thead>
          <tbody>
            {data.comparisons.map((comp) => {
              const isPositive = comp.change >= 0;
              const changeColor = isPositive ? 'text-green-600' : 'text-red-600';
              const bgColor = isPositive ? 'bg-green-50' : 'bg-red-50';

              return (
                <tr key={comp.period} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-2 font-medium text-gray-900">
                    {periodLabels[comp.period] || comp.period}
                  </td>
                  <td className="py-3 px-2 text-right text-gray-700">
                    ${formatPrice(comp.startPrice)}
                  </td>
                  <td className="py-3 px-2 text-right text-gray-700">
                    ${formatPrice(comp.endPrice)}
                  </td>
                  <td className={`py-3 px-2 text-right font-medium ${changeColor}`}>
                    {isPositive ? '+' : ''}${formatPrice(comp.change)}
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className={`inline-block px-2 py-1 rounded text-sm font-medium ${bgColor} ${changeColor}`}>
                      {formatPercentage(comp.changePercent)}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right text-gray-700">
                    ${formatPrice(comp.high)}
                  </td>
                  <td className="py-3 px-2 text-right text-gray-700">
                    ${formatPrice(comp.low)}
                  </td>
                  <td className="py-3 px-2 text-right text-gray-700">
                    {comp.dataPoints}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
